/// <amd-module name="Comments/Redux/CommentsSelectors" />
import { ICommentArtifactInfo } from "Comments/Common/Contracts";
import { ICommentReactionTypeInfo, IReactionTypeToInfoMap } from "Comments/Reactions/Contracts";
import { createReactionsMap } from "Comments/Reactions/Util";
import { ICommentItemReduxState, ICommentsReduxAwareState, ICommentsReduxState, ICommentsReduxStateMap } from "Comments/Redux/Contracts";
import produce from "immer";

function _getCommentsReduxStateMap(awareState: ICommentsReduxAwareState): ICommentsReduxStateMap {
    awareState = awareState || {};
    return awareState.commentsReduxStateMap || {};
}

/**
 * Gets the state for a specific comment in the store for the given artifact and comment ID.
 * @param awareState The root level redux aware state
 * @param artifactInfo Artifact info
 * @param commentId Comment ID
 * @returns Comment state if it exists in the store (check comment.isDeleted if needed),
 * null if the comment was deleted without first fetched (because only ID is needed to delete),
 * or undefined if it hasn't been fetched at all.
 */
export function getCommentItemState(
    awareState: ICommentsReduxAwareState,
    artifactInfo: ICommentArtifactInfo,
    commentId: number
): ICommentItemReduxState | null | undefined {
    const stateMap = _getCommentsReduxStateMap(awareState);
    return getCommentItemStateFromMap(stateMap, artifactInfo, commentId);
}

/**
 * Gets the state for all the comments in the store for the given artifact.
 * @param state Comments state
 * @param artifactInfo Artifact info
 */
export function getCommentsStateFromMap(
    state: ICommentsReduxStateMap,
    artifactInfo: ICommentArtifactInfo
): ICommentsReduxState | undefined {
    return state[getArtifactKey(artifactInfo)];
}

/**
 * Gets the state for a specific comment in the store for the given artifact and comment ID.
 * @param state Comments state
 * @param artifactInfo Artifact info
 * @param commentId Comment ID
 * @returns Comment state if it exists in the store (check comment.isDeleted if needed),
 * null if the comment was deleted without first fetched (because only ID is needed to delete),
 * or undefined if it hasn't been fetched at all.
 */
export function getCommentItemStateFromMap(
    state: ICommentsReduxStateMap,
    artifactInfo: ICommentArtifactInfo,
    commentId: number
): ICommentItemReduxState | null | undefined {
    const commentsState = getCommentsStateFromMap(state, artifactInfo);
    if (commentsState && commentsState.comments) {
        return commentsState.comments[commentId];
    }

    return undefined;
}

export function getCommentReactionInfoMap(
    awareState: ICommentsReduxAwareState,
    artifactInfo: ICommentArtifactInfo,
    commentId: number,
    isPendingReactionActionUpdatesInitiated: boolean,
    pendingReactionActions: number[]
): IReactionTypeToInfoMap {
    const commentItemState = getCommentItemState(awareState, artifactInfo, commentId);
    let commentReactionTypeToInfoMap: IReactionTypeToInfoMap = {};
    if (commentItemState) {
        if (commentItemState.commentReactionTypeToInfoMap) {
            commentReactionTypeToInfoMap = commentItemState.commentReactionTypeToInfoMap;
        } else {
            commentReactionTypeToInfoMap = createReactionsMap(commentItemState.comment.reactions);
        }
    }

    // Modify the map only if there are pending reaction actions
    if (
        !isPendingReactionActionUpdatesInitiated &&
        pendingReactionActions &&
        pendingReactionActions.some((pendingAction) => pendingAction !== 0)
    ) {
        pendingReactionActions.forEach((pendingAction, reactionType) => {
            if (pendingAction === 0) {
                return;
            }

            commentReactionTypeToInfoMap = produce(commentReactionTypeToInfoMap, (draft) => {
                const reactionTypeInfo = draft[reactionType];
                const isCurrentUserEngaged = pendingAction === 1 ? true : false;
                if (reactionTypeInfo) {
                    reactionTypeInfo.totalCount += pendingAction;
                    reactionTypeInfo.isCurrentUserEngaged = isCurrentUserEngaged;
                } else {
                    draft[reactionType] = {
                        totalCount: Math.max(pendingAction, 0), // pendingAction shouldn't be -1 when there are no existing reactions
                        isCurrentUserEngaged,
                        engagedUsers: [] // Ok to fetch users with call to Server later on
                    } as ICommentReactionTypeInfo;
                }
            });
        });
    }

    return commentReactionTypeToInfoMap;
}

/**
 * Delimiter used to create the artifact key.
 */
export const ArtifactKeyDelimiter = "/";

/**
 * Gets the artifact key used to store and retrieve commment states.
 * @param artifactInfo Artifact info
 * @returns Artifact key
 */
export function getArtifactKey(artifactInfo: ICommentArtifactInfo): string {
    const { projectId, artifactKind, artifactId } = artifactInfo;
    return `${projectId}${ArtifactKeyDelimiter}${artifactKind}${ArtifactKeyDelimiter}${artifactId}`;
}
